/**
 * Marking a quotation Won from the Requirements board, and re-syncing one afterwards
 * (Akhil, 2026-10-16; the manual Sync 2026-10-24).
 *
 * Won used to fire on one click while Rejected got a dialog. Now that a Won also pushes the
 * whole enquiry chain to G6 through the connector (`src/lib/api/g6Push.ts`), it gets a confirm
 * first — the decision is irreversible and now leaves the building.
 *
 * ---- Why a manual Sync exists at all ----
 *
 * The push on Won is best effort: it runs after the decision is saved and a failure is a
 * warning, never a rolled-back Won. That is the right trade — a connector outage must not undo
 * a business decision — but it leaves no second chance. Before this button, an enquiry whose
 * push warned could only be re-sent by marking it Won again, which it already is.
 *
 * So Sync is a RETRY of that same call, offered exactly where the failure was reported.
 */

/** The confirm shown before a Won. Names the enquiry, the customer and both consequences. */
export function confirmWonMessage(requirement: { id: string; customer_name: string; quotation_id: string | null }): string {
  return (
    `Mark ${requirement.id} for ${requirement.customer_name} as Won?\n\n` +
    `${requirement.quotation_id ?? 'The quotation'} is marked Ordered and the enquiry, supplier quotations, comparison ` +
    `and quotation are synced to the ERP. This cannot be undone.`
  )
}

/** The toast after a Won — one line, the sync outcome folded in rather than a second toast.
 *  "Synced", never "pushed to G6" (Akhil, 2026-10-16): the purchaser knows the ERP, not the connector. */
export function wonToast(enquiryId: string, pushWarning: string | null): string {
  return pushWarning ? `${enquiryId} marked Won — ${pushWarning}` : `${enquiryId} marked Won and synced to the ERP`
}

/* --------------------------------------------------------------------------------------------
   The manual Sync
   -------------------------------------------------------------------------------------------- */

/**
 * Whether a requirement can be re-synced.
 *
 * Won only: the chain the connector sends is Enquiry → Supplier Quotation → Comparison →
 * Quotation, and before a decision the last of those is not final. A Won without a quotation id
 * cannot happen through the board (deciding requires a sent quotation) but is guarded anyway —
 * the id is what the chain is addressed by, and a button that cannot work should not be shown.
 */
export function canSyncRequirement(requirement: { status: string; quotation_id: string | null }): boolean {
  return requirement.status === 'Won' && Boolean(requirement.quotation_id)
}

/** The confirm shown before a manual Sync. Sending data outward is worth one question. */
export function confirmSyncMessage(requirement: { id: string; customer_name: string }): string {
  return (
    `Sync ${requirement.id} for ${requirement.customer_name} to the ERP?\n\n` +
    `The enquiry, its supplier quotations, the comparison and the quotation are sent again. ` +
    `Use this when the sync failed or the ERP is missing the order.`
  )
}

/** The toast after a manual Sync — the same vocabulary as `wonToast`, never "pushed to G6". */
export function syncToast(enquiryId: string, pushWarning: string | null): string {
  return pushWarning ? `${enquiryId} — ${pushWarning}` : `${enquiryId} synced to the ERP`
}
